import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus } from 'lucide-react';
import Modal from '../common/Modal';
import projectService from '../../services/projectService';
import { cn } from '../../lib/cn';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const ROLE_OPTIONS = [
  { value: 'review', label: 'Review (by Reviewer role)' },
  { value: 'contribution', label: 'Contribution (by Contributor role)' },
];

const STEP_STATUS_LABELS = {
  completed: 'completed',
  in_progress: 'in progress',
  pending: 'pending',
  unassigned: 'unassigned',
};

function StepRow({ step, index, assigneeList, isLocked, onChange, onRemove, canRemove }) {
  const status = (step.step_status || 'unassigned').toLowerCase();
  const isInProgress = status === 'in_progress';

  return (
    <div className="flex flex-col">
      <div
        className={cn(
          'rounded-lg border p-3 flex flex-wrap items-center gap-3',
          isInProgress
            ? 'border-orange-500 bg-orange-50/50 dark:bg-orange-950/20 dark:border-orange-600'
            : 'border-gray-200 dark:border-neutral-700 bg-gray-50/50 dark:bg-neutral-900/50'
        )}
      >
        <select
          value={step.role || ''}
          onChange={(e) => onChange(index, { ...step, role: e.target.value || 'review' })}
          disabled={isLocked}
          className={cn(
            'flex-1 min-w-[120px] rounded-md border bg-white dark:bg-neutral-900 px-3 py-2 text-sm',
            isLocked || status === 'completed' ? 'text-gray-400 dark:text-neutral-500 border-gray-200 dark:border-neutral-700' : 'text-foreground border-border'
          )}
        >
          <option value="">Select Role</option>
          {ROLE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={step.assignee_id || ''}
          onChange={(e) => onChange(index, { ...step, assignee_id: e.target.value || null })}
          disabled={isLocked}
          className={cn(
            'flex-1 min-w-[140px] rounded-md border bg-white dark:bg-neutral-900 px-3 py-2 text-sm',
            isLocked || status === 'completed' ? 'text-gray-400 dark:text-neutral-500 border-gray-200 dark:border-neutral-700' : 'text-foreground border-border'
          )}
        >
          <option value="">Select Assignee</option>
          {(assigneeList || []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.display_name || u.name || u.email || String(u.id).slice(0, 8)}
            </option>
          ))}
        </select>
        <span
          className={cn(
            'text-xs shrink-0',
            isInProgress ? 'text-orange-700 dark:text-orange-400' : 'text-muted-foreground'
          )}
        >
          Status: {STEP_STATUS_LABELS[status] || status}
        </span>
        {canRemove && !isLocked && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="text-muted-foreground hover:text-destructive text-xs"
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

export default function SetWorkflowModal({
  isOpen,
  onClose,
  projectId,
  projectType,
  isLocked = false,
  onSaved,
}) {
  const [steps, setSteps] = useState([]);
  const [assigneeList, setAssigneeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    if (!projectId) {
      setLoading(false);
      setSteps([{ role: 'review', assignee_id: null, step_status: 'unassigned' }]);
      return;
    }
    setError(null);
    setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const [stepsRes, usersRes] = await Promise.all([
          projectService.getWorkflowSteps(projectId),
          axios.get(`${API_BASE}/admin/users?minimal=true`),
        ]);
        if (cancelled) return;
        const list = Array.isArray(usersRes.data?.users) ? usersRes.data.users : [];
        setAssigneeList(list);
        const stepList = Array.isArray(stepsRes) ? stepsRes : [];
        if (stepList.length === 0) {
          setSteps([{ role: 'review', assignee_id: null, step_status: 'unassigned' }]);
        } else {
          setSteps(
            stepList.map((s) => ({
              id: s.id,
              role: (s.role || 'review').toLowerCase(),
              assignee_id: s.assignee_id || null,
              step_status: (s.step_status || 'unassigned').toLowerCase(),
              assignee_display_name: s.assignee_display_name,
              assignee_email: s.assignee_email,
            }))
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message || 'Failed to load workflow');
          setSteps([{ role: 'review', assignee_id: null, step_status: 'unassigned' }]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, projectId]);

  const updateStep = (index, next) => {
    setSteps((prev) => {
      const out = [...prev];
      out[index] = { ...out[index], ...next };
      return out;
    });
  };

  const removeStep = (index) => {
    if (steps.length <= 1) return;
    setSteps((prev) => prev.filter((_, i) => i !== index));
  };

  const addStep = () => {
    setSteps((prev) => [...prev, { role: 'review', assignee_id: null, step_status: 'unassigned' }]);
  };

  const handleSave = async () => {
    if (isLocked || saving) return;
    const payload = steps.map((s) => {
      const status = (s.step_status || 'unassigned').toLowerCase();
      const hasAssignee = !!s.assignee_id;
      // Treat "Save" with an assignee as assigned: use 'pending' instead of 'unassigned'
      const stepStatus = hasAssignee && status === 'unassigned' ? 'pending' : status;
      return {
        role: (s.role || 'review').toLowerCase(),
        assignee_id: s.assignee_id || null,
        step_status: stepStatus,
      };
    });
    setSaving(true);
    setError(null);
    try {
      await projectService.setWorkflowSteps(projectId, payload);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  const noProject = isOpen && !projectId;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" showCloseButton={true}>
      <div className="p-6 overflow-y-auto max-h-[90vh] bg-white dark:bg-neutral-950 rounded-lg">
        <h2 className="text-lg font-semibold text-foreground mb-4">Set Workflow</h2>
        {noProject ? (
          <>
            <p className="text-muted-foreground mb-6">Create a project first. After creation, you can set workflow from the project list or project detail page.</p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
              >
                Close
              </button>
            </div>
          </>
        ) : loading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : (
          <>
            <div className="space-y-2">
              {steps.map((step, index) => (
                <React.Fragment key={index}>
                  <StepRow
                    step={step}
                    index={index}
                    assigneeList={assigneeList}
                    isLocked={isLocked}
                    onChange={updateStep}
                    onRemove={removeStep}
                    canRemove={steps.length > 1}
                  />
                  {index < steps.length - 1 && (
                    <div className="flex justify-center py-0.5">
                      <div className="w-0 h-3 border-l-2 border-dashed border-gray-300 dark:border-neutral-600" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
            {!isLocked && (
              <div className="mt-4 flex flex-col items-center gap-2">
                <button
                  type="button"
                  onClick={addStep}
                  className="w-10 h-10 rounded-full border-2 border-dashed border-gray-300 dark:border-neutral-600 flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                  title="Add step"
                >
                  <Plus className="w-5 h-5" />
                </button>
                <p className="text-xs text-muted-foreground">By clicking, adds more steps</p>
              </div>
            )}
            {error && (
              <p className="mt-3 text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-md border border-border bg-background text-foreground text-sm font-medium hover:bg-muted"
              >
                Cancel
              </button>
              {!isLocked && (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
